import type { ReactNode } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { buyerSubmissionsApi, type BuyerSubmissionDetailDto } from "@/api/buyers";
import { useBreadcrumbs } from "@/components/layout/BreadcrumbsContext";
import { PageHeader } from "@/components/layout/PageHeader";
import { buttonVariants } from "@/design-system/components/ui";
import { buyerSubmissionKeys } from "@/hooks/buyers/buyerKeys";
import { useAuth } from "@/hooks/useAuth";
import {
  listingDisplayName,
  listingShortName,
  shortenListingName,
} from "@/lib/listingDisplay";
import { SubmissionStatusControl } from "./SubmissionStatusControl";
import {
  SubmissionArchivedBanner,
  SubmissionRowMenu,
  useSubmissionRemoval,
  type SubmissionRemoval,
  type SubmissionRemovalTarget,
} from "./submission/submissionArchive";
import { parseTabKey, SubmissionDetailBody, type TabKey } from "./SubmissionDetailBody";

/**
 * One submission — a buyer presented to a listing, and the negotiation that
 * followed.
 *
 * <p>This is where the conversation about a listing lives, rather than on the
 * listing itself: a listing is shared, and what one buyer's specialist said to
 * the broker is not the other buyer's business.</p>
 *
 * <p>The route owns two things and nothing else: the page chrome, and the tab
 * in the URL. Everything below the header is {@link SubmissionDetailBody}, which
 * a drawer elsewhere renders with the same props — so a submission read in a
 * drawer and a submission read at its own address can never show different
 * facts.</p>
 */
export function BuyerSubmissionDetailPage() {
  const { id = "" } = useParams<{ id: string }>();
  const [params, setParams] = useSearchParams();

  // Tab in the URL, so a link to "that submission's documents" is a link rather
  // than an instruction. Resolved at PARSE time: a post-mount effect that wrote
  // the default back would re-key every read on this page after first render and
  // fetch the record twice.
  const tab = parseTabKey(params.get("tab"));
  const setTab = (next: TabKey) => {
    const p = new URLSearchParams(params);
    if (next === "overview") p.delete("tab");
    else p.set("tab", next);
    setParams(p, { replace: true });
  };

  // The same read the body runs, on the same key — React Query serves both
  // subscribers from one request, and the header re-titles itself when a status
  // change updates the cache. Reading the cache directly instead would not
  // subscribe, and the header would go stale.
  const submission = useQuery({
    queryKey: buyerSubmissionKeys.detail(id),
    queryFn: () => buyerSubmissionsApi.get(id),
    enabled: Boolean(id),
  });

  const s = submission.data;
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  // D3/D5 — Archive / Restore / Delete from the header; a deleted submission's
  // page would be not-found, so a delete lands on the buyer instead.
  const removal = useSubmissionRemoval({
    canDeleteAtAll: hasPermission("Submissions.Delete"),
    onDeletedOne: () => {
      if (s) navigate(`/buyers/${s.buyerLeadId}?tab=submissions`, { replace: true });
    },
  });
  const target: SubmissionRemovalTarget | null = s
    ? {
        id: s.id,
        listingName: s.listing?.displayName?.trim() || listingDisplayName(s.listing),
        isArchived: s.isArchived,
        canArchive: s.canArchive,
        archiveBlockedReason: s.archiveBlockedReason,
        canDelete: s.canDelete,
        deleteBlockedReason: s.deleteBlockedReason,
      }
    : null;

  /*
    The buyer and listing pages name their record in the trail; this one did
    not, which is the inconsistency the breadcrumb work existed to remove.

    `null` while loading is load-bearing — the alternative is a crumb that reads
    as one record's name and then changes to another. The leaf names the LISTING
    rather than the submission's own id, because that is what a specialist calls
    this row; the buyer is one click away in the header.
  */
  useBreadcrumbs(
    s
      ? [
          { label: "Submissions", href: "/buyer-submissions" },
          // The SAME resolution the header title uses, so the trail and the
          // title can never name the listing two different ways.
          { label: shortenListingName(s.listing?.displayName) ?? listingShortName(s.listing) },
        ]
      : null,
  );

  return (
    <>
      {/* Loading, missing and failed are all reported by the body, which has to
          report them anyway for the drawer that has no header at all. A header
          rendered over any of the three would have to invent a title. */}
      {s && target && <SubmissionPageHeader s={s} target={target} removal={removal} />}
      {s && target && (
        <SubmissionArchivedBanner
          target={target}
          archivedAtUtc={s.archivedAtUtc}
          archivedByName={s.archivedByName}
          removal={removal}
        />
      )}
      {removal.node}

      <SubmissionDetailBody submissionId={id} activeTab={tab} onTabChange={setTab} />
    </>
  );
}

/**
 * Route chrome — deliberately NOT part of the shared body.
 *
 * <p>A drawer brings its own frame and its own way out, so a second page title
 * inside one is noise. The status control and the two cross-links live here for
 * the same reason they live in every other detail header, which means a host
 * that wants them somewhere else supplies its own.</p>
 */
function SubmissionPageHeader({
  s,
  target,
  removal,
}: {
  s: BuyerSubmissionDetailDto;
  target: SubmissionRemovalTarget;
  removal: SubmissionRemoval;
}) {
  /*
    🔴 SHORT, and applied INSIDE the pairing rather than to it. A submission has
    no name of its own — it IS `{buyer} → {listing}` — so the listing's whole
    marketplace title landed in a 40px display-caps `h1` and wrapped to five
    lines. Shortening the composed string instead would cut at the first
    separator in EITHER half.

    The full title rides the `title` attribute: the listing is a foreign record
    here, so unlike the listing's own page there is nowhere else on screen the
    pitch can be read.
  */
  const listingName = shortenListingName(s.listing?.displayName) ?? listingShortName(s.listing);
  const fullListingName = s.listing?.displayName?.trim() || listingDisplayName(s.listing);

  return (
    <PageHeader
      eyebrow="Submission"
      title={
        <span title={`${s.buyerDisplayName} → ${fullListingName}`}>
          {`${s.buyerDisplayName} → ${listingName}`}
        </span>
      }
      actions={
        <div className="flex flex-wrap items-center gap-space-2">
          <SubmissionStatusControl
            submissionId={s.id}
            status={s.status}
            canEdit={s.canEdit}
          />
          <LinkButton to={`/buyers/${s.buyerLeadId}`}>Open buyer</LinkButton>
          <LinkButton to={`/listings/${s.listingId}`}>Open listing</LinkButton>
          <SubmissionRowMenu
            target={target}
            removal={removal}
            testId="submission-header-actions"
          />
        </div>
      }
    />
  );
}

/**
 * A cross-link that looks and focuses like the controls beside it.
 *
 * <p>🔴 <b>Why not a bare styled <c>&lt;Link&gt;</c>, which is what these were.</b>
 * The two navigations sit in a header row next to a real <c>&lt;Button&gt;</c>,
 * and they were plain gold underlined text. Visually that is a deliberate
 * choice one could defend; the focus ring is not. Every control in this app
 * takes <c>focus-visible:shadow-goldRing</c> from <c>buttonVariants</c>, so a
 * keyboard user tabbing along this row got the gold ring, the gold ring, and
 * then the browser's default outline — the two most useful escapes from this
 * page being the two that looked broken.</p>
 *
 * <p>It stays an anchor rather than becoming a <c>&lt;Button onClick=navigate&gt;</c>,
 * because these ARE addresses: middle-click, "open in new tab" and copy-link all
 * work on the first and none of them work on the second. There is no
 * <c>asChild</c> in this design system, so the classes are applied directly —
 * <c>buttonVariants</c> rather than a retyped class string, so a change to the
 * button's focus treatment reaches these too.</p>
 */
export function LinkButton({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link to={to} className={buttonVariants({ variant: "secondary", size: "sm" })}>
      {children}
    </Link>
  );
}

export type { BuyerSubmissionDetailDto };
